import React from "react";
import Seo from "../components/Seo";
import "../css/Documentation.css";

export default class XMQ_persistence extends React.Component
{
    render()
    {
        return <div key="persistence" className="XMQ" style={{textAlign: "left", padding: 8}}>
            <Seo title="XMQ Message Persistence: Durable and Super-Durable"
                 description="How XMQ avoids unnecessary persistence writes with a 10 ms delivery window, what can be lost on failure, and how to configure Redis durability."
                 keywords="MQTT durability, MQTT persistence, Redis appendfsync, durable MQTT broker, XMQ"
                 path="/xmq_persistence"/>
            <h1>Message Persistence: Durable and Super-Durable</h1>

            <p>
                A persistent MQTT broker promises that a QoS&nbsp;1 or 2 message for a persistent
                session is not lost. How far that promise reaches depends on what fails, and it is
                kept in two places: the broker, which writes a record of each delivery to Redis, and
                Redis, which decides when those records reach the disk. Each has its own setting,
                and together they make two modes worth naming.
            </p>

            <p>
                <b>This page describes XMQ 0.9.20 and later.</b> The Redis half applies to any
                version. The broker behavior described here changed in 0.9.19 and 0.9.20; older
                versions can lose queued deliveries on a broker crash.
            </p>

            <p style={{borderLeft: "4px solid #2878b5", padding: "8px 12px", background: "#f3f8fc"}}>
                <b>10 ms write-behind window.</b> XMQ gives a queued delivery up to 10&nbsp;ms
                before writing its persistence record. If the subscriber acknowledges it during
                that window, XMQ skips the database write because the record is no longer needed
                for redelivery. This reduces write load on Redis while keeping the same broker
                crash-loss bound: <code>persistence.max_queued_writes</code>.
            </p>

            <h3>What each mode can lose</h3>

            <table className="table table-sm" style={{maxWidth: 900}}>
                <thead>
                <tr>
                    <th>What fails</th>
                    <th>Durable</th>
                    <th>Super-Durable</th>
                    <th>Redis defaults (not recommended)</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>The broker process</td>
                    <td>At most <code>max_queued_writes</code> messages</td>
                    <td>At most <code>max_queued_writes</code> messages</td>
                    <td>At most <code>max_queued_writes</code> messages</td>
                </tr>
                <tr>
                    <td>The Redis process</td>
                    <td>Nothing: the operating system still holds what Redis wrote</td>
                    <td>Nothing</td>
                    <td>Everything since the last snapshot - a minute or more</td>
                </tr>
                <tr>
                    <td>Power, or the machine Redis runs on</td>
                    <td>Up to one second of writes</td>
                    <td>Nothing</td>
                    <td>Everything since the last snapshot - a minute or more</td>
                </tr>
                </tbody>
            </table>

            <p>
                The last column is what an unconfigured Redis does: it saves a snapshot now and
                then, and whatever happened since is gone if the machine goes down. No broker
                setting can make up for that, which is why Redis's own configuration is half of
                this page.
            </p>

            <h3>The broker's window: <code>max_queued_writes</code></h3>

            <p>
                <code>persistence.max_queued_writes</code> is how many delivery records may be
                waiting for Redis at once. While the window has room, messages are delivered without
                waiting for their records, so writes to Redis overlap instead of queueing one behind
                another. After 10&nbsp;ms, XMQ writes each still-needed record; if the subscriber
                acknowledged the delivery first, the write is skipped. When the window is full, the broker stops reading from the
                publishers that would add to it until Redis catches up, so a slow Redis slows the
                publishers down and nobody else.
            </p>

            <p>
                The same number is the most the broker can lose if its process dies: records it
                had not yet handed to Redis. At 0 every message waits for its own record, and
                nothing is lost with the broker - at the price of one Redis round trip per message,
                which caps the rate well below what the other settings allow. <b>1000</b> is the
                value both modes below use: at 60,000 messages a second it is under 20
                milliseconds of traffic.
            </p>

            <p>
                It is set on the Persistence screen of the configuration interface, or in{" "}
                <code>xmq_server.conf</code>:
            </p>
            <pre>{`"persistence": {
    "enabled": true,
    "redis_uri": "redis://localhost:6379",
    "max_queued_writes": 1000
}`}</pre>

            <h3>Durable</h3>

            <p>
                Redis appends every write to its log and has the operating system flush the log to
                disk once a second. Losing the machine loses at most that second; losing only the
                Redis process loses nothing, because what it wrote is already with the operating
                system. This is the mode for almost every deployment that needs persistence at all:
                it costs little over not syncing, and the gap it leaves is one second on a power
                failure.
            </p>
            <pre>{`# /etc/redis/redis.conf
appendonly yes
appendfsync everysec`}</pre>

            <h3>Super-Durable</h3>

            <p>
                Redis flushes its log to disk before it confirms a write. Nothing Redis confirmed is
                lost, whatever fails, and what the broker can lose is exactly its window. The price
                is a disk flush in the path of every batch of writes, so both the rate a broker can
                sustain and its latency depend on how fast the disk completes a flush. On a disk
                without power-loss protection a flush waits for the data to reach the medium; on an
                enterprise SSD with power-loss protection it returns as soon as the drive has the
                data, and is many times faster. Choose this mode where losing a single
                acknowledged message costs more than the throughput - and run it on such a disk.
            </p>
            <pre>{`# /etc/redis/redis.conf
appendonly yes
appendfsync always`}</pre>

            <h3>What it costs</h3>

            <p>
                Durable, measured on AWS with XMQ 0.9.19: <b>60,000 persistent messages a second at
                210us</b>, flat for ten minutes, 120,000 persistent sessions, 216% CPU. Details on
                the <a href="/xmq_tests_persistence">Persistence test page</a>.
            </p>

            <h3>After changing Redis</h3>

            <p>
                Restart Redis for the configuration to take effect
                (<code>sudo systemctl restart redis-server</code> on most Linux distributions). The
                first start with <code>appendonly yes</code> creates the log from the data Redis
                already has. The Docker composition in the XMQ repository
                (<code>docker/docker-compose.yml</code>) starts Redis with{" "}
                <code>appendonly yes</code>, and so runs Durable out of the box.
            </p>
        </div>;
    }
}
